/**
 * Marimba-like tones for Tuchi melody (Web Audio FM, GM-style program 12/13).
 * No sample files — synth only.
 */

/** C5 / E5 / G5 / C6 — bright marimba range, easy to tell apart. */
const NOTE_FREQ_HZ: Record<1 | 2 | 3 | 4, number> = {
  1: 523.25,
  2: 659.25,
  3: 783.99,
  4: 1046.5,
};

type AudioContextCtor = typeof AudioContext;

let sharedCtx: AudioContext | null = null;

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as Window & {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function getCtx(): AudioContext | null {
  const Ctor = getAudioContextCtor();
  if (!Ctor) {
    return null;
  }
  if (!sharedCtx) {
    sharedCtx = new Ctor();
  }
  return sharedCtx;
}

/** Unlock audio after a user gesture (VK WebView / autoplay policy). */
export async function ensureTuchiAudioReady(): Promise<void> {
  const ctx = getCtx();
  if (!ctx) {
    return;
  }
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      // ignore — playback will no-op until context is allowed
    }
  }
}

function asNoteId(note: number): 1 | 2 | 3 | 4 | null {
  if (note === 1 || note === 2 || note === 3 || note === 4) {
    return note;
  }
  return null;
}

export function tuchiNoteFrequencyHz(note: number): number | null {
  const id = asNoteId(note);
  return id ? NOTE_FREQ_HZ[id] : null;
}

/**
 * Play one marimba strike (FM: modulator ≈ 3.5× carrier, fast attack, wood decay).
 */
export function playTuchiMarimbaNote(note: number, durationSec = 0.42): void {
  const freq = tuchiNoteFrequencyHz(note);
  const ctx = getCtx();
  if (freq == null || !ctx) {
    return;
  }

  const now = ctx.currentTime;
  const duration = Math.max(0.12, durationSec);

  const master = ctx.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(0.55, now + 0.008);
  master.gain.exponentialRampToValueAtTime(0.001, now + duration);
  master.connect(ctx.destination);

  // Carrier — fundamental
  const carrier = ctx.createOscillator();
  carrier.type = 'sine';
  carrier.frequency.setValueAtTime(freq, now);

  // Modulator — metallic wood partials (classic marimba FM ratio ~3.5)
  const mod = ctx.createOscillator();
  mod.type = 'sine';
  mod.frequency.setValueAtTime(freq * 3.5, now);

  const modGain = ctx.createGain();
  modGain.gain.setValueAtTime(freq * 2.2, now);
  modGain.gain.exponentialRampToValueAtTime(freq * 0.15, now + duration * 0.55);

  mod.connect(modGain);
  modGain.connect(carrier.frequency);

  // Soft 2nd partial for body
  const partial = ctx.createOscillator();
  partial.type = 'sine';
  partial.frequency.setValueAtTime(freq * 2.0, now);
  const partialGain = ctx.createGain();
  partialGain.gain.setValueAtTime(0.18, now);
  partialGain.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.7);

  carrier.connect(master);
  partial.connect(partialGain);
  partialGain.connect(master);

  carrier.start(now);
  mod.start(now);
  partial.start(now);

  const stopAt = now + duration + 0.02;
  carrier.stop(stopAt);
  mod.stop(stopAt);
  partial.stop(stopAt);
}

/** Short low thud when the player misses a note. */
export function playTuchiMarimbaFail(): void {
  const ctx = getCtx();
  if (!ctx) {
    return;
  }
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(120, now);
  osc.frequency.exponentialRampToValueAtTime(70, now + 0.18);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.35, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.24);
}

/** Short ascending arpeggio on victory. */
export function playTuchiMarimbaVictory(): void {
  const notes: Array<1 | 2 | 3 | 4> = [1, 2, 3, 4];
  notes.forEach((note, i) => {
    window.setTimeout(() => {
      playTuchiMarimbaNote(note, 0.35);
    }, i * 90);
  });
}
